// TTS audio manager for the practice conversation.
// Design goals (per owner spec):
//  - Autoplay new AI partner turns only after an explicit user gesture.
//  - Never autoplay old history on session load; only future arrivals.
//  - Single active playback; stop previous before starting a new one.
//  - Stop when the learner starts recording, backgrounds the app, navigates
//    away, or explicitly pauses/ends the session.
//  - Deduplicate TTS by (sessionId, turnIndex, speed); reuse in-memory blob.
//  - Handle browser autoplay policy: on rejection expose a "needsUnlock" flag
//    so the UI can prompt an explicit "Tap to enable audio".
//  - Never speak learner text.
import { speak } from "@/lib/api";

const cache = new Map(); // key -> { blob, url }
let currentAudio = null;
let currentToken = 0; // increments when a request should be superseded/cancelled

const MAX_CACHE_ENTRIES = 24;

const cacheKey = (sessionId, turnIndex, speed) =>
  `${sessionId || "-"}::${turnIndex}::${speed}`;

const rememberBlob = (key, blob) => {
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const firstKey = cache.keys().next().value;
    const evicted = cache.get(firstKey);
    if (evicted?.url) URL.revokeObjectURL(evicted.url);
    cache.delete(firstKey);
  }
  const url = URL.createObjectURL(blob);
  cache.set(key, { blob, url });
  return url;
};

export const stopAll = () => {
  try {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
  } catch (e) {
    /* ignore */
  }
  currentAudio = null;
  currentToken += 1; // supersede any in-flight fetch
};

export const clearCache = () => {
  for (const { url } of cache.values()) {
    try {
      URL.revokeObjectURL(url);
    } catch (e) {
      /* ignore */
    }
  }
  cache.clear();
};

/**
 * Play TTS for a given text.
 * @param {object} opts
 * @param {string} opts.text
 * @param {number} opts.speed
 * @param {string} [opts.sessionId]
 * @param {number|string} [opts.turnIndex]
 * @returns {Promise<{played: boolean, needsUnlock: boolean, error?: any}>}
 */
export const playTTS = async ({ text, speed = 1.0, sessionId, turnIndex }) => {
  if (!text) return { played: false, needsUnlock: false };
  const key = cacheKey(sessionId, turnIndex, speed);
  const token = ++currentToken;

  // Stop any current playback before starting a new one (single voice).
  try {
    if (currentAudio) {
      currentAudio.pause();
    }
  } catch (e) {
    /* ignore */
  }

  let url;
  try {
    if (cache.has(key)) {
      url = cache.get(key).url;
    } else {
      const blob = await speak(text, speed);
      // If a stopAll or newer request came in while we were fetching, drop this.
      if (token !== currentToken) return { played: false, needsUnlock: false };
      url = rememberBlob(key, blob);
    }
  } catch (err) {
    return { played: false, needsUnlock: false, error: err };
  }

  if (token !== currentToken) return { played: false, needsUnlock: false };

  const audio = new Audio(url);
  currentAudio = audio;
  try {
    await audio.play();
    return { played: true, needsUnlock: false };
  } catch (err) {
    // Autoplay policy rejection: caller should surface a tap-to-enable UI.
    return { played: false, needsUnlock: true, error: err };
  }
};

// Small helper: primes an audio element as a user-gesture side effect so a
// subsequent programmatic play() is allowed by the autoplay policy on some
// browsers. Silent/short buffer play. Safe to call multiple times.
let unlocked = false;
export const unlockAudio = () => {
  if (unlocked) return true;
  try {
    const ctx = window.AudioContext || window.webkitAudioContext;
    if (ctx) {
      const c = new ctx();
      const buffer = c.createBuffer(1, 1, 22050);
      const src = c.createBufferSource();
      src.buffer = buffer;
      src.connect(c.destination);
      src.start(0);
      unlocked = true;
    }
  } catch (e) {
    /* ignore */
  }
  return unlocked;
};

export const isUnlocked = () => unlocked;
